const selectors = {
  sizeGuideContent: '[data-size-guide-content]',
  unitToggle: '[data-unit-toggle]'
}

const classes = {
  visuallyHidden: 'visually-hidden'
}

class SizeGuideDrawer extends BaseDrawer {
  constructor() {
    const globalSelectors = {
      triggerButton: '[data-size-guide-button]',
      drawer: '[data-size-guide-drawer]',
      overlay: '[data-size-guide-drawer-overlay]',
    };
    super(globalSelectors);
  }

  connectedCallback() {
    this.tableData = this.convertCsvToArrays();
    this.currentUnit = 'CM';
    this.unitToggle = this.querySelector(selectors.unitToggle);

    this.tableHeaders= this.tableData.map((row) => row[0]);

    this.cmColumnIndexes = this.tableHeaders?.reduce((indexes, header, index) => {
      const headerText = String(header || '').toUpperCase();
      if (headerText === 'CM' || headerText.includes(' CM') || headerText.includes('(CM)')) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    this.buildTable();

    // Check if CM columns exist, hide toggle if not
    if (this.cmColumnIndexes.length === 0) {
      this.unitToggle?.parentElement?.classList.add('hidden');
    }

    this.unitToggle?.addEventListener('change', this.toggleUnit.bind(this));
  }

  buildTable() {
    const content = this.querySelector(selectors.sizeGuideContent);
    if (!this.tableData?.length) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <div class="component-size-table__table-wrapper">
        <table class="component-size-table__table">
          ${this.createTableBody(this.tableData)}
        </table>
      </div>
    `;

    content.appendChild(wrapper);
  }

  createTableBody(bodyRows) {
    const headerRow = bodyRows.map((row) => row[0]);
    const dataRows = bodyRows.map(row => row.slice(1));
    const bodyRowsCount = Math.max(...dataRows.map(row => row.length));

    const createTableCell = (value, type = 'data') => {
      return type === 'header' ? `<th>${value}</th>` : `<td>${value}</td>`;
    };

    const createTableBody = () => {
      const tableBodyRows = [];

      for (let i = 0; i < bodyRowsCount; i++) {
        const cells = dataRows.map(row => `<td>${row[i] ?? ''}</td>`).join('');
        tableBodyRows.push(`<tr>${cells}</tr>`);
      }

      return tableBodyRows.join('');
    }

    return `
      <thead>
        <tr>${headerRow.map((header) => createTableCell(header, 'header')).join('')}</tr>
      </thead>
      <tbody>
        ${createTableBody()}
      </tbody>
    `;
  }

  convertCsvToArrays() {
    const content = this.querySelector(selectors.sizeGuideContent);
    if (!content) return [];

    const items = content.dataset.sizeGuideContent;
    if (!items) return [];

    const values = items.replaceAll(', ', ',').split('\n');

    return values.map((row) =>
      row.split(',').map((value) => {
        const numValue = Number(value);
        return numValue ? numValue : value;
      })
    );
  }

  toggleUnit() {
    this.currentUnit = this.currentUnit === 'CM' ? 'IN' : 'CM';

    if (!this.cmColumnIndexes.length) return;

    this.cmColumnIndexes.forEach(colIndex => {
      const cmRow = this.tableData[colIndex];
      const cmValues = cmRow.slice(1);

      const cells = this.querySelectorAll(`tbody tr td:nth-child(${colIndex + 1})`);

      cells.forEach((cell, rowIndex) => {
        const cmValue = cmValues[rowIndex];
        if (typeof cmValue === 'number') {
          const value = this.currentUnit === 'IN'
            ? (cmValue / 2.54).toFixed(1)
            : cmValue;
          cell.textContent = value;
        }
      });

      const header = this.querySelector(`thead th:nth-child(${colIndex + 1})`);
      if (header) {
        const originalHeader = this.tableHeaders[colIndex];
        header.textContent = this.currentUnit === 'IN'
          ? originalHeader.replace(/CM/i, 'IN').replace(/\(CM\)/i, '(IN)')
          : originalHeader;
      }
    });
  }
}

customElements.define('size-guide-drawer', SizeGuideDrawer);

/**
 * Size Guide V2 - Advanced size guide with:
 * - Size system dropdown (auto-detected from CSV headers)
 * - Sticky first column with horizontal scroll
 * - CM/INCH unit conversion
 * - Scroll progress indicator
 * - localStorage persistence
 */

const SIZE_SYSTEM_KEYWORDS = [
  'Aus/NZ', 'AU/NZ', 'AU', 'US', 'US Mens', 'US Womens',
  'UK', 'EU', 'EUR', 'Alpha', 'Numerical', 'FR', 'IT', 'JP',
  'Size',
];

const V2_STORAGE_KEYS = {
  sizeSystem: 'sizeGuideV2.sizeSystem',
  unit: 'sizeGuideV2.unit',
};

class SizeGuideDrawerV2 extends BaseDrawer {
  constructor() {
    const globalSelectors = {
      triggerButton: '[data-size-guide-button]',
      drawer: '[data-size-guide-drawer]',
      overlay: '[data-size-guide-drawer-overlay]',
    };
    super(globalSelectors);
  }

  connectedCallback() {
    this.tableContainer = this.querySelector('[data-table-container]');
    this.dropdownWrapper = this.querySelector('[data-size-system-dropdown]');
    this.dropdownTrigger = this.querySelector('[data-dropdown-trigger]');
    this.dropdownValue = this.querySelector('[data-dropdown-value]');
    this.dropdownList = this.querySelector('[data-dropdown-list]');
    this.unitToggle = this.querySelector('[data-unit-toggle]');
    this.scrollIndicator = this.querySelector('[data-scroll-indicator]');
    this.scrollIndicatorBar = this.querySelector('[data-scroll-indicator-bar]');

    if (!this.tableContainer) return;

    this.tableData = this.parseCSV();
    if (!this.tableData) return;

    this.classifyColumns();
    this.currentUnit = 'cm';
    this.selectedSystemIndex = null;

    const preferences = this.loadPreferences();
    this.currentUnit = preferences.unit;

    this.buildDropdown(preferences.sizeSystem);
    this.initDropdown();
    this.buildTable();
    this.initScrollIndicator();

    if (this.unitToggle) {
      if (this.currentUnit === 'in') {
        this.unitToggle.checked = true;
      }
      this.unitToggle.addEventListener('change', this.toggleUnit.bind(this));
    }
  }

  parseCSV() {
    const raw = this.tableContainer.dataset.sizeGuideContent;
    if (!raw) return null;

    const lines = raw.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2) return null;

    const headers = lines[0].split(',').map(h => h.trim());
    const rows = lines.slice(1).map(line =>
      line.split(',').map(cell => cell.trim())
    );

    return { headers, rows };
  }

  classifyColumns() {
    if (!this.tableData) return;

    this.sizeSystems = [];
    this.measurements = [];

    this.tableData.headers.forEach((header, index) => {
      const isSizeSystem = SIZE_SYSTEM_KEYWORDS.some(keyword =>
        header === keyword || header.startsWith(keyword)
      );

      if (isSizeSystem) {
        this.sizeSystems.push({ header, index });
      } else {
        this.measurements.push({ header, index });
      }
    });
  }

  buildDropdown(preferredSystem) {
    if (!this.dropdownList || this.sizeSystems.length === 0) return;

    this.dropdownList.innerHTML = '';

    const inner = document.createElement('div');
    inner.className = 'size-guide-v2__dropdown-list-inner';

    let selectedIndex = this.sizeSystems[0].index;
    if (preferredSystem) {
      const match = this.sizeSystems.find(s => s.header === preferredSystem);
      if (match) selectedIndex = match.index;
    }

    this.sizeSystems.forEach(system => {
      const item = document.createElement('li');
      item.className = 'size-guide-v2__dropdown-item';
      item.dataset.value = system.index;
      item.textContent = system.header;
      item.setAttribute('role', 'option');
      item.addEventListener('click', () => this.selectItem(system.index, system.header));
      inner.appendChild(item);
    });

    this.dropdownList.appendChild(inner);

    this.selectedSystemIndex = selectedIndex;
    const selectedSystem = this.sizeSystems.find(s => s.index === selectedIndex);
    if (this.dropdownValue && selectedSystem) {
      this.dropdownValue.textContent = selectedSystem.header;
    }

    // Hide dropdown if only 1 size system
    const dropdownContainer = this.dropdownWrapper?.closest('.size-guide-v2__dropdown');
    if (dropdownContainer && this.sizeSystems.length <= 1) {
      dropdownContainer.style.display = 'none';
    }
  }

  initDropdown() {
    if (!this.dropdownTrigger || !this.dropdownWrapper) return;

    this.dropdownTrigger.addEventListener('click', () => this.toggleDropdown());

    document.addEventListener('click', (e) => {
      if (!this.dropdownWrapper.contains(e.target)) {
        this.closeDropdown();
      }
    });

    this.dropdownWrapper.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDropdown();
    });
  }

  toggleDropdown() {
    const isOpen = this.dropdownWrapper.classList.contains('is-open');
    if (isOpen) {
      this.closeDropdown();
    } else {
      this.dropdownWrapper.classList.add('is-open');
      this.dropdownTrigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeDropdown() {
    this.dropdownWrapper.classList.remove('is-open');
    this.dropdownTrigger?.setAttribute('aria-expanded', 'false');
  }

  selectItem(index, label) {
    this.selectedSystemIndex = index;
    if (this.dropdownValue) this.dropdownValue.textContent = label;
    this.closeDropdown();
    this.buildTable();
    this.savePreferences();
    this.initScrollIndicator();
  }

  buildTable() {
    if (!this.tableData || !this.tableContainer) return;

    const selectedIndex = this.selectedSystemIndex ?? this.sizeSystems[0]?.index;
    const selectedSystem = this.sizeSystems.find(s => s.index === selectedIndex);
    if (!selectedSystem) return;

    const columns = [selectedSystem, ...this.measurements];

    const headerCells = columns.map((col, i) => {
      const stickyClass = i === 0 ? ' size-guide-v2__table-cell--sticky' : '';
      return `<div class="size-guide-v2__table-cell size-guide-v2__table-cell--header${stickyClass}">${col.header}</div>`;
    }).join('');

    const bodyRows = this.tableData.rows.map(row => {
      const cells = columns.map((col, i) => {
        let value = row[col.index] ?? '';
        if (i > 0 && this.currentUnit === 'in') {
          value = this.convertValue(value, 'in');
        }
        const stickyClass = i === 0 ? ' size-guide-v2__table-cell--sticky' : '';
        return `<div class="size-guide-v2__table-cell${stickyClass}">${value}</div>`;
      }).join('');
      return `<div class="size-guide-v2__table-row">${cells}</div>`;
    }).join('');

    this.tableContainer.innerHTML = `
      <div class="size-guide-v2__table" style="--size-guide-columns: ${columns.length}">
        <div class="size-guide-v2__table-row size-guide-v2__table-row--header">
          ${headerCells}
        </div>
        ${bodyRows}
      </div>
    `;
  }

  convertValue(value, targetUnit) {
    if (!value || value === '') return '';

    const rangeMatch = value.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)$/);
    if (rangeMatch) {
      const min = this.convertSingleValue(rangeMatch[1], targetUnit);
      const max = this.convertSingleValue(rangeMatch[2], targetUnit);
      return `${min} - ${max}`;
    }

    return this.convertSingleValue(value, targetUnit);
  }

  convertSingleValue(value, targetUnit) {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;

    if (targetUnit === 'in') {
      return (numValue / 2.54).toFixed(1);
    }

    return value;
  }

  toggleUnit() {
    this.currentUnit = this.unitToggle.checked ? 'in' : 'cm';
    this.buildTable();
    this.savePreferences();
    this.initScrollIndicator();
  }

  initScrollIndicator() {
    if (!this.scrollIndicator || !this.scrollIndicatorBar) return;

    const tableWrapper = this.tableContainer;

    requestAnimationFrame(() => {
      const isScrollable = tableWrapper.scrollWidth > tableWrapper.clientWidth;

      if (!isScrollable) {
        this.scrollIndicator.style.display = 'none';
        return;
      }

      this.scrollIndicator.style.display = '';
      
      // Calculate and apply sticky cell offset
      const stickyCell = tableWrapper.querySelector('.size-guide-v2__table-cell--sticky');
      if (stickyCell) {
        const stickyWidth = stickyCell.offsetWidth;
        this.scrollIndicator.style.marginLeft = `${stickyWidth}px`;
        this.scrollIndicator.style.width = `calc(100% - ${stickyWidth}px)`;
      } else {
        this.scrollIndicator.style.marginLeft = '';
        this.scrollIndicator.style.width = '';
      }
      
      this.updateScrollIndicator();

      if (this._scrollHandler) {
        tableWrapper.removeEventListener('scroll', this._scrollHandler);
      }

      this._scrollHandler = () => this.updateScrollIndicator();
      tableWrapper.addEventListener('scroll', this._scrollHandler, { passive: true });
    });
  }

  updateScrollIndicator() {
    const tableWrapper = this.tableContainer;
    if (!tableWrapper || !this.scrollIndicatorBar) return;

    const stickyCell = tableWrapper.querySelector('.size-guide-v2__table-cell--sticky');
    const stickyWidth = stickyCell ? stickyCell.offsetWidth : 0;

    const scrollWidth = tableWrapper.scrollWidth;
    const clientWidth = tableWrapper.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) return;

    // Calculate the scrollable width (excluding sticky column)
    const scrollableWidth = scrollWidth - stickyWidth;
    const visibleScrollableWidth = clientWidth - stickyWidth;

    const barWidthPercent = (visibleScrollableWidth / scrollableWidth) * 100;
    const barPositionPercent = (tableWrapper.scrollLeft / maxScroll) * (100 - barWidthPercent);

    this.scrollIndicatorBar.style.width = `${barWidthPercent}%`;
    this.scrollIndicatorBar.style.left = `${barPositionPercent}%`;
  }

  loadPreferences() {
    return {
      sizeSystem: localStorage.getItem(V2_STORAGE_KEYS.sizeSystem) || null,
      unit: localStorage.getItem(V2_STORAGE_KEYS.unit) || 'cm',
    };
  }

  savePreferences() {
    const selectedSystem = this.sizeSystems.find(s => s.index === this.selectedSystemIndex);
    if (selectedSystem) {
      localStorage.setItem(V2_STORAGE_KEYS.sizeSystem, selectedSystem.header);
    }
    localStorage.setItem(V2_STORAGE_KEYS.unit, this.currentUnit);
  }
}

customElements.define('size-guide-drawer-v2', SizeGuideDrawerV2);